/**
 * Closed allow-list HTML sanitizer for block.text:
 *
 *   <strong>, </strong>, <em>, </em>,
 *   <span style="color:#RRGGBB">, <span style="font-size:Npt">,
 *   <span style="color:#RRGGBB;font-size:Npt">, </span>,
 *   <span class="ref-chip" data-ref-id="ID">  (ID matches [A-Za-z0-9_-]{1,64}, url:<rowId>, or ref:<rowId>)
 *
 * Plus plain text and {{ref:uuid}} tokens (passed through verbatim).
 * Anything else is stripped. Hand-rolled — no DOMPurify dependency.
 */
const COLOR_RE = /^#[0-9a-f]{6}$/;
const FONT_SIZE_RE = /^\d+pt$/;
const REF_ID_RE = /^(?:url:[A-Za-z0-9_-]{1,58}|ref:[A-Za-z0-9_-]{1,58}|[A-Za-z0-9_-]{1,64})$/;
const REF_KIND_RE = /^(internal|url)$/;
const REF_STATUS_RE = /^(valid|ignored)$/;
const ALLOWED_TAGS = new Set(['strong', 'em', 'span', 'b', 'i', 'u', 'br']);

function escapeText(s: string): string {
  return s
    // Preserve entities already emitted by edit-text-serialize (sanitizeHtml /
    // escapeHtml) so view-mode sanitization does not double-encode them.
    .replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[0-9a-fA-F]+);)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function parseSpanStyle(attrs: string): string | null {
  const matches = [
    ...attrs.matchAll(/\bstyle\s*=\s*"([^"]*)"|\bstyle\s*=\s*'([^']*)'/g),
  ];
  if (matches.length === 0) return null;
  const raw = (matches[0][1] ?? matches[0][2] ?? '').trim();
  if (!raw) return null;
  const props = raw.split(';').map(s => s.trim()).filter(Boolean);
  const kept: string[] = [];
  for (const p of props) {
    const idx = p.indexOf(':');
    if (idx < 0) continue;
    const key = p.substring(0, idx).trim().toLowerCase();
    const value = p.substring(idx + 1).trim().toLowerCase();
    if (key === 'color' && COLOR_RE.test(value)) kept.push(`color:${value}`);
    else if (key === 'font-size' && FONT_SIZE_RE.test(value))
      kept.push(`font-size:${value}`);
  }
  if (kept.length === 0) return null;
  return kept.join(';');
}

function parseChipAttrs(
  attrs: string,
): {
  refId: string;
  broken: boolean;
  lost: boolean;
  kind: string | null;
  status: string | null;
} | null {
  // Extract class — must be present exactly once, exactly "ref-chip".
  const classMatches = [
    ...attrs.matchAll(/\bclass\s*=\s*"([^"]*)"|\bclass\s*=\s*'([^']*)'/g),
  ];
  if (classMatches.length !== 1) return null;
  const className = (classMatches[0][1] ?? classMatches[0][2] ?? '').trim();
  if (className !== 'ref-chip') return null;

  // Extract data-ref-id — must be present exactly once, must match REF_ID_RE.
  const refIdMatches = [
    ...attrs.matchAll(/\bdata-ref-id\s*=\s*"([^"]*)"|\bdata-ref-id\s*=\s*'([^']*)'/g),
  ];
  if (refIdMatches.length !== 1) return null;
  const refId = (refIdMatches[0][1] ?? refIdMatches[0][2] ?? '').trim();
  if (!REF_ID_RE.test(refId)) return null;

  // Extract optional data-broken — if present, must appear exactly once with value "true".
  const brokenMatches = [
    ...attrs.matchAll(/\bdata-broken\s*=\s*"([^"]*)"|\bdata-broken\s*=\s*'([^']*)'/g),
  ];
  if (brokenMatches.length > 1) return null;
  let broken = false;
  if (brokenMatches.length === 1) {
    const brokenVal = (brokenMatches[0][1] ?? brokenMatches[0][2] ?? '').trim();
    if (brokenVal !== 'true') return null;
    broken = true;
  }

  const lostMatches = [
    ...attrs.matchAll(/\bdata-lost\s*=\s*"([^"]*)"|\bdata-lost\s*=\s*'([^']*)'/g),
  ];
  if (lostMatches.length > 1) return null;
  let lost = false;
  if (lostMatches.length === 1) {
    const lostVal = (lostMatches[0][1] ?? lostMatches[0][2] ?? '').trim();
    if (lostVal !== 'true') return null;
    lost = true;
  }

  // Extract optional data-kind — if present, must match (internal|url).
  const kindMatches = [
    ...attrs.matchAll(/\bdata-kind\s*=\s*"([^"]*)"|\bdata-kind\s*=\s*'([^']*)'/g),
  ];
  if (kindMatches.length > 1) return null;
  let kind: string | null = null;
  if (kindMatches.length === 1) {
    const kindVal = (kindMatches[0][1] ?? kindMatches[0][2] ?? '').trim();
    if (!REF_KIND_RE.test(kindVal)) return null;
    kind = kindVal;
  }

  // Extract optional data-status — if present, must match (accepted|ignored).
  const statusMatches = [
    ...attrs.matchAll(/\bdata-status\s*=\s*"([^"]*)"|\bdata-status\s*=\s*'([^']*)'/g),
  ];
  if (statusMatches.length > 1) return null;
  let status: string | null = null;
  if (statusMatches.length === 1) {
    const statusVal = (statusMatches[0][1] ?? statusMatches[0][2] ?? '').trim();
    if (!REF_STATUS_RE.test(statusVal)) return null;
    status = statusVal;
  }

  // The chip whitelist allows: `class`, `data-ref-id`, `data-broken`, `data-lost`,
  // `data-kind` (internal | url), `data-status` (valid | ignored).
  // `contenteditable` (emitted by the edit-mode renderer) is silently dropped.
  const allAttrs = [...attrs.matchAll(/\b([\w:-]+)\s*=/g)];
  const allowed = new Set([
    'class',
    'data-ref-id',
    'contenteditable',
    'data-broken',
    'data-lost',
    'data-kind',
    'data-status',
  ]);
  for (const m of allAttrs) {
    if (!allowed.has(m[1].toLowerCase())) return null;
  }

  return { refId, broken, lost, kind, status };
}

// Void elements that have no closing tag and no content to strip
const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

// Tags whose entire subtree (open tag + content + close tag) must be removed
const SUPPRESS_CONTENT_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'noscript',
  'template', 'svg', 'math',
]);

export function sanitizeBlockHtml(input: string): string {
  const out: string[] = [];
  let cursor = 0;
  // Stack of allowed open tags (for closing-tag matching)
  const openStack: string[] = [];
  // Stack of disallowed open tags whose content we are suppressing
  const suppressStack: string[] = [];

  for (const match of input.matchAll(/<(\/?)([a-zA-Z]+)([^>]*)>/g)) {
    const [full, slash, name, attrs] = match;
    const start = match.index ?? 0;
    const lname = name.toLowerCase();
    const isClose = slash === '/';

    // Text before this tag — only emit when not suppressing
    if (start > cursor && suppressStack.length === 0) {
      out.push(escapeText(input.substring(cursor, start)));
    }
    cursor = start + full.length;

    // Handle closing tags
    if (isClose) {
      if (
        suppressStack.length > 0 &&
        suppressStack[suppressStack.length - 1] === lname
      ) {
        // Closing a suppressed tag — stop suppressing
        suppressStack.pop();
      } else if (
        suppressStack.length === 0 &&
        ALLOWED_TAGS.has(lname) &&
        openStack[openStack.length - 1] === lname
      ) {
        openStack.pop();
        out.push(`</${lname}>`);
      }
      // All other closing tags (non-allowed, non-suppressed) are silently dropped
      continue;
    }

    // Opening tag — if we're suppressing, ignore everything
    if (suppressStack.length > 0) {
      // Track nested suppressed tags so we pop at the right closing tag
      if (SUPPRESS_CONTENT_TAGS.has(lname) && !VOID_ELEMENTS.has(lname)) {
        suppressStack.push(lname);
      }
      continue;
    }

    // Not suppressing — check if this tag starts suppression
    if (SUPPRESS_CONTENT_TAGS.has(lname)) {
      suppressStack.push(lname);
      continue;
    }

    // Void elements and other disallowed tags: just drop the tag, keep subsequent text
    if (!ALLOWED_TAGS.has(lname)) continue;

    if (VOID_ELEMENTS.has(lname)) {
      out.push(`<${lname}>`);
      continue;
    }

    // Allowed opening tag — handle <span> specially (chip vs style-only)
    if (lname === 'span') {
      const chip = parseChipAttrs(attrs);
      if (chip !== null) {
        openStack.push('span');
        const brokenAttr = chip.broken ? ' data-broken="true"' : '';
        const lostAttr = chip.lost ? ' data-lost="true"' : '';
        const kindAttr = chip.kind ? ` data-kind="${chip.kind}"` : '';
        const statusAttr = chip.status ? ` data-status="${chip.status}"` : '';
        out.push(
          `<span class="ref-chip"${brokenAttr}${lostAttr}${kindAttr}${statusAttr} data-ref-id="${chip.refId}">`,
        );
        continue;
      }
      const style = parseSpanStyle(attrs);
      if (style === null) continue;
      openStack.push('span');
      out.push(`<span style="${style}">`);
      continue;
    }

    // Allowed non-span opening tag
    openStack.push(lname);
    out.push(`<${lname}>`);
  }

  // Remaining text after the last tag (only if not suppressing)
  if (cursor < input.length && suppressStack.length === 0) {
    out.push(escapeText(input.substring(cursor)));
  }
  // Close any unclosed allowed tags
  while (openStack.length > 0) {
    const t = openStack.pop();
    out.push(`</${t!}>`);
  }
  return out.join('');
}
